/**
 * 大框内的三个入口 chip —— 产品 / 叠 logo·二维码 / 特别要求(2026-07-30)。
 *
 * ═══ 为什么从「三个段」收进框里 ═══════════════════════════════════════════════
 * 它们回答的是同一个问题:「这张图除了这段话,还要带什么」。摆成页面上三个独立的段
 * 之后,用户写完文案直接点生成 —— 07-29 T1 的用户原话就是「②③ 一次都没打开过」。
 * 那一批的处置是把段序重排,但段还是段;这一批收进框里:**不点不占版面,点了就地展开**。
 *
 * ★设过值的 chip 变主色 + 写出当前值 —— 折叠可以,失忆不行:
 *   「我这次带了产品吗、印不印电话」必须一眼看得见(与折叠段必给摘要同一条纪律)。
 * ★不做成第二入口:原来那三处(段②产品 / 段③叠什么 / 特别要求块)同步删掉,
 *   同一件事两个入口迟早漂成两套状态。
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, Plus, Stamp } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

function Chip({
  active,
  icon,
  children,
  onClick,
}: {
  active?: boolean;
  icon: ReactNode;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        active
          ? "border-primary/50 bg-primary/10 font-medium text-primary"
          : "border-border/60 bg-background/50 text-muted-foreground hover:border-primary/40 hover:text-foreground"
      )}
    >
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{children}</span>
    </button>
  );
}

export function PromptEntries({
  productSlot,
  productLabel,
  brandSlot,
  brandLabel,
  brandActive,
  extraOnly = false,
  extraElsewhere = false,
  extraConstraint,
  setExtraConstraint,
  disabled,
}: {
  /** 产品选择器本体(抽屉,自带触发器) —— 直接摆成 chip 的位置。 */
  productSlot?: ReactNode;
  productLabel?: string;
  /** 品牌叠加物面板(点开才渲染)。 */
  brandSlot?: ReactNode;
  brandLabel?: string;
  brandActive?: boolean;
  /**
   * 只出「特别要求」——**改图那条路**用。
   * 产品与叠加物对改图没有意义(那是改这张图,不是造一张新的),但硬性要求有。
   */
  extraOnly?: boolean;
  /**
   * 2026-08-01 —— 这页**别处**已经有产品入口了(商品主图那条路把它做成了页面顶部的
   * 独立块,不走 `productSlot`)。不传的话 `!productSlot` 会误判成「这页没有入口」,
   * 于是在商品主图页上挂一句「去商品主图 ▸」—— 你已经在那儿了。
   */
  extraElsewhere?: boolean;
  extraConstraint: string;
  setExtraConstraint: (v: string) => void;
  disabled?: boolean;
}) {
  const [extraOpen, setExtraOpen] = useState(false);
  const hasExtra = extraConstraint.trim().length > 0;

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {!extraOnly && (
          <>
            {/* ① 产品 —— 抽屉自带触发器,这里只负责它站在这一行。
                ★2026-08-01 起只有**商品主图**那条路会传它:产品树是「资料从哪来」,
                  由用途带出来,不带用途的自由创作页面上不该有它。
                  不传就整个不占位,不是渲染一个空 span。 */}
            {productSlot && (
              <span className="inline-flex items-center" title={productLabel}>
                {productSlot}
              </span>
            )}

            {/* ② 叠 logo / 二维码 —— 点开才渲染面板 */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed={brandActive}
                  className={cn(
                    "inline-flex max-w-[220px] items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                    brandActive
                      ? "border-primary/50 bg-primary/10 font-medium text-primary"
                      : "border-border/60 bg-background/50 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                  )}
                >
                  <Stamp className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{brandLabel}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[360px] p-3">
                {brandSlot}
              </PopoverContent>
            </Popover>
          </>
        )}

        {/* ③ 特别要求 —— 未填只是一行加号;★填过就把内容摘要写在 chip 上,
            它逐字进图,收起来看不见等于用户不知道自己这次带了什么。 */}
        <Chip
          active={hasExtra}
          icon={<Plus className="h-3.5 w-3.5" />}
          onClick={() => setExtraOpen((v) => !v)}
        >
          {hasExtra ? extraConstraint.trim().slice(0, 18) : "特别要求"}
        </Chip>
      </div>

      {!extraOnly && !productSlot && !extraElsewhere && (
        // ★不让人走进死胡同:产品入口迁去商品主图之后,这里必须说得出它去哪了。
        //   与 T2I↔I2I 互跳同一个手法。改图那条路不提 —— 卡墙上那些"目的"卡承诺的
        //   全是从零创作的 T2I 形态,挑完回来仍然不是「改这张图」。
        // ★`extraElsewhere`:商品主图那条路的产品入口是**页面顶部的独立块**,
        //   不走 `productSlot`,光看 `!productSlot` 会以为这页没有入口 ——
        //   结果就是在商品主图页上挂一句「去商品主图 ▸」(你已经在那儿了)。
        <p className="text-[12px] text-muted-foreground">
          要带商品资料?
          <Link
            to="/ai-haibao"
            className="ml-0.5 font-semibold text-primary underline-offset-4 hover:underline"
          >
            去商品主图 ▸
          </Link>
        </p>
      )}

      {(extraOpen || hasExtra) && (
        <div className="rounded-lg border border-border/60 bg-background/50 p-2">
          <textarea
            value={extraConstraint}
            onChange={(e) => setExtraConstraint(e.target.value)}
            rows={2}
            maxLength={200}
            disabled={disabled}
            placeholder="必须满足的硬性要求,例:必须出现「顺丰包邮」、logo 放右下角(逐字进图,AI 不改写)"
            className="w-full resize-y bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
          />
        </div>
      )}
    </div>
  );
}

export { PackageSearch };
